/**
 * Helper functions for mapping raw API objects to more convenient versions (e.g., converting from strings to other
 * data types). If there is a helper function for a particular Undivided model, then you should probably wrap any
 * received JSON object around that function before using it.
 */
import isValid from 'date-fns/isValid'
import parse from 'date-fns/parse'
import startOfDay from 'date-fns/startOfDay'
import isPlainObject from 'lodash/isPlainObject'

import { CAN_BE_NAVIGATOR, CAN_BE_SUPERUSER, CAN_BE_CERTIFIED, CAN_BE_ADMIN, RESOURCE_LINK } from './arch/constants'

import {
  ACCOMMODATION_TYPE_ACCOMMODATION,
  ACCOMMODATION_TYPE_MODIFICATION,
  GOAL_TYPE_PRIMARY,
  GOAL_TYPE_SECONDARY
} from './arch/iepConstants'

import { TASK_TYPE_NEW_MESSAGES } from './arch/taskConstants'
import { listToMap } from './common'

import {
  ASSET_SIZE_ORIGINAL,
  ASSET_SIZE_1X,
  ASSET_SIZE_2X,
  ASSET_SIZE_3X,
  DATE_FORMAT_ISO,
  FALLBACK_CHILD_NAME
} from './constants'

import { getBirthdayDate } from './domain/client'

import {
  IPP_TYPE_OUTCOME,
  IPP_TYPE_PRIORITY,
  IPP_TYPE_SUPPORT,
  PLAN_TYPE_GOAL,
  PLAN_TYPE_SERVICE
} from './iep/constants'

import { clientList } from './navigation/support/common'
import { ATTACHMENT_TYPE_POST } from './subcomponents/attachment/constants'

const convertToDateIfNecessary = (source, dateProperty) => {
  const dateString = source[dateProperty]
  if (dateString && typeof dateString === 'string') {
    source[dateProperty] = new Date(dateString)

    // Preserve the original in case it is used for cases like a cursor.
    source[`${dateProperty}__string`] = dateString
  }
}

const post = post => {
  // First, assign default values for certain attributes if they are missing.
  post.additionalProperties = post.additionalProperties ?? {}
  post.assetDetails = post.assetDetails ?? []
  post.types = post?.type?.split('.') ?? []

  // Next, normalize dates.
  post.assetDetails.forEach(assetDetail => convertToDateIfNecessary(assetDetail, 'createdAt'))
  convertToDateIfNecessary(post, 'createdAt')
  convertToDateIfNecessary(post, 'referenceTimestamp')
  convertToDateIfNecessary(post, 'lastUpdatedAt')
  return post
}

const event = event => {
  convertToDateIfNecessary(event, 'startTime')
  convertToDateIfNecessary(event, 'endTime')
  event.household = event.household || { id: event.householdId }

  // Attachments can either come from post objects or post ID strings. Either way, converge into attachment
  // objects in order to match the expectations of our attachment comopnents.
  const { postIds } = event
  event.posts = event.posts || (postIds ? postIds.map(postId => ({ id: postId })) : [])
  event.attachments = event.posts.map(post => ({ type: ATTACHMENT_TYPE_POST, post }))
  return event
}

const convertCommonTimestamps = source => {
  convertToDateIfNecessary(source, 'createdAt')
  convertToDateIfNecessary(source, 'updatedAt')
  return source
}

const contact = contact => {
  convertToDateIfNecessary(contact, 'createdAt')
  return contact
}

const goal = goal => {
  convertCommonTimestamps(goal)
  convertToDateIfNecessary(goal, 'completedAt')
  convertToDateIfNecessary(goal, 'dueDate')
  return goal
}

const guide = guide => {
  convertCommonTimestamps(guide)

  // Guide variants from the API use “guideId” to hold the ID of their “root/source” guide.
  // Store these in a different attribute to match the web app code better.
  const { id, guideId, variantSourceId, variants } = guide
  if (guideId && !variantSourceId) {
    // Make the assignment only if a guideId is present and that variantSourceId hasn’t been
    // assigned yet.
    guide.variantSourceId = guideId
  }

  // “Parent” guides may have a `variants` array listing their variants. For uniformity with
  // the top-level model, we make sure these variant stubs also have `guideId` and
  // `variantSourceId` attributes.
  if (Array.isArray(variants)) {
    variants.forEach(variant => {
      variant.guideId = id
      variant.variantSourceId = id
    })
  }

  return guide
}

const household = household => {
  convertToDateIfNecessary(household, 'createdAt')
  convertToDateIfNecessary(household, 'kickstartDate')
  convertToDateIfNecessary(household, 'kickstartEndDate')
  convertToDateIfNecessary(household, 'navigatorHoldEnds')
  convertToDateIfNecessary(household, 'trialStartDate')
  convertToDateIfNecessary(household, 'trialEndDate')

  if (Array.isArray(household.clients)) {
    household.clients = clientList(household.clients)
  }

  return household
}

const indexToNumber = (item, index) => ({ ...item, number: index + 1 })

// TODO This one can majorly use a unit test.
/**
 * Convenience function for gathering up the performance areas referenced by a particular collection
 * of “performance area containers” (e.g., goals, services, etc.) and collecting them within the
 * performance area itself. The result stored in the given `performanceAreaMap` argument.
 *
 * @param {array} performanceAreaContainers objects that have been associated with performance areas
 * @param {object} performanceAreaMap ID-to-object hash of performance areas
 * @param {string} idsAttribute attribute name under which to store associated performance area container IDs
 */
const gatherPerformanceAreaIds = (performanceAreaContainers, performanceAreaMap, idsAttribute) => {
  performanceAreaContainers.forEach(({ id, performanceAreas }) => {
    if (performanceAreas) {
      performanceAreas.forEach(performanceArea => {
        const { id: performanceAreaId } = performanceArea
        if (!performanceAreaMap[performanceAreaId]) {
          performanceAreaMap[performanceAreaId] = { ...performanceArea }
        }

        const performanceAreaEntry = performanceAreaMap[performanceAreaId]
        if (!performanceAreaEntry[idsAttribute]) {
          performanceAreaEntry[idsAttribute] = []
        }

        performanceAreaEntry[idsAttribute].push(id)
      })
    }
  })
}

/**
 * Utility function for associating goal and service/accommodation/modification IDs with the
 * performance area objects that they reference.
 *
 * @param {object} iep the IEP to process
 * @returns an object mapping performance area keys to values, with the values having goal and service
 *          ID arrays
 */
const getPerformanceAreaGoalAndServiceIds = iep => {
  const { goals, plops, services, accommodations, modifications } = iep

  // Gather up all performance areas.
  const performanceAreaMap = {}

  gatherPerformanceAreaIds(goals, performanceAreaMap, 'goalIds')
  gatherPerformanceAreaIds(plops, performanceAreaMap, 'plopIds')
  gatherPerformanceAreaIds(services, performanceAreaMap, 'serviceIds')
  gatherPerformanceAreaIds(accommodations, performanceAreaMap, 'accommodationIds')
  gatherPerformanceAreaIds(modifications, performanceAreaMap, 'modificationIds')

  return performanceAreaMap
}

/**
 * Function that groups secondary goals by their primary goal IDs
 *
 * @param {array} goals array of all goals (primary and secondary)
 * @returns map where keys are primary goal IDs and values are arrays of secondary goals
 */
const groupSecondaryGoalsByPrimary = goals => {
  return goals.reduce((map, goal) => {
    if (goal.type === GOAL_TYPE_SECONDARY && goal.annualIepGoalId) {
      if (!map[goal.annualIepGoalId]) {
        map[goal.annualIepGoalId] = []
      }
      map[goal.annualIepGoalId].push(goal)
    }
    return map
  }, {})
}

const iep = iep => {
  const { goals, plops, accommodations, recommendations, concerns } = iep

  // As of UND-4026, only primary goals are to be visible to the user.
  iep.allGoals = goals
  iep.goals = goals.filter(({ type }) => type === GOAL_TYPE_PRIMARY)

  // Yank out the different types of services and supplement them with numbers.
  iep.services = iep.services.map(indexToNumber)
  iep.allAccommodations = accommodations

  iep.accommodations = accommodations
    .filter(service => service.type === ACCOMMODATION_TYPE_ACCOMMODATION)
    .map(indexToNumber)

  iep.modifications = accommodations
    .filter(service => service.type === ACCOMMODATION_TYPE_MODIFICATION)
    .map(indexToNumber)

  // Gather up all nested recommendations for easier tallying.
  iep.goalRecommendations = iep.goals.flatMap(({ recommendations }) => recommendations)
  iep.accommodationRecommendations = iep.accommodations.flatMap(({ recommendations }) => recommendations)
  iep.modificationRecommendations = iep.modifications.flatMap(({ recommendations }) => recommendations)
  iep.serviceRecommendations = iep.services.flatMap(({ recommendations }) => recommendations)
  iep.plopRecommendations = plops.flatMap(({ recommendations }) => recommendations)

  // Provide maps of certain collections for easier lookup.
  iep.performanceAreas = getPerformanceAreaGoalAndServiceIds(iep)
  iep.plopsMap = listToMap(plops)
  iep.recommendationsMap = listToMap(recommendations)
  iep.concernsMap = listToMap(concerns)
  iep.goalsMap = listToMap(goals)
  iep.servicesMap = listToMap(iep.services)
  iep.accommodationsMap = listToMap(iep.accommodations)
  iep.modificationsMap = listToMap(iep.modifications)
  iep.secondaryGoalsMap = groupSecondaryGoalsByPrimary(goals)

  return iep
}

const ipp = ipp => {
  const { priorities, outcomes, goals, services, supports, recommendations, concerns } = ipp

  // Each subarray is numbered within its own type and tagged with a type identifier.
  ipp.priorities = priorities.map(indexToNumber).map(priority => ({ ...priority, type: IPP_TYPE_PRIORITY }))
  ipp.outcomes = outcomes.map(indexToNumber).map(outcome => ({ ...outcome, type: IPP_TYPE_OUTCOME }))
  ipp.goals = goals.map(indexToNumber).map(goal => ({ ...goal, type: PLAN_TYPE_GOAL }))
  ipp.services = services.map(indexToNumber).map(service => ({ ...service, type: PLAN_TYPE_SERVICE }))
  ipp.supports = supports.map(indexToNumber).map(support => ({ ...support, type: IPP_TYPE_SUPPORT }))

  // Priorities, outcomes, and goals are displayed in a single sequence, so we combine them here.
  ipp.pogs = [...ipp.priorities, ...ipp.outcomes, ...ipp.goals]

  // Same for services and supports.
  ipp.servicesSupports = [...ipp.services, ...ipp.supports]

  // Gather up all nested recommendations for easier tallying, both per-type and combined.
  ipp.priorityRecommendations = ipp.priorities.flatMap(({ recommendations }) => recommendations)
  ipp.goalRecommendations = ipp.goals.flatMap(({ recommendations }) => recommendations)
  ipp.outcomeRecommendations = ipp.outcomes.flatMap(({ recommendations }) => recommendations)
  ipp.pogRecommendations = ipp.pogs.flatMap(({ recommendations }) => recommendations)
  ipp.serviceSupportRecommendations = ipp.servicesSupports.flatMap(({ recommendations }) => recommendations)

  // Provide maps of certain collections for easier lookup.
  ipp.recommendationsMap = listToMap(recommendations)
  ipp.concernsMap = listToMap(concerns)
  ipp.pogsMap = listToMap(ipp.pogs)
  ipp.servicesSupportsMap = listToMap(ipp.servicesSupports)

  return ipp
}

const step = step => {
  convertToDateIfNecessary(step, 'finishedAt')

  // Special cases:
  //
  // The Undivided API only accepts 'YYYY-MM-DD' for the goal date when POSTing or PATCHing steps but
  // returns a full ISO string, _with UTC time zone_, when GETting them. This effectively offsets the goal
  // date by the local time zone in relation to UTC when in reality the goal date will always be perceived
  // as local by the user. To get around this, we truncate everything after the date.
  if (step.goalDate) {
    const dateOnlyFormatLength = DATE_FORMAT_ISO.length

    let goalDateStr = step.goalDate
    goalDateStr = goalDateStr.length > dateOnlyFormatLength ? goalDateStr.substr(0, dateOnlyFormatLength) : goalDateStr

    const parsedDate = parse(goalDateStr, DATE_FORMAT_ISO, startOfDay(new Date()))
    step.goalDate = isValid(parsedDate) ? parsedDate : null
  }

  return step
}

const task = task => {
  convertCommonTimestamps(task)
  convertToDateIfNecessary(task, 'finishedAt')
  convertToDateIfNecessary(task, 'targetDate')

  // Port specifications date(s) if needed.
  const { type, specifications } = task
  if (type === TASK_TYPE_NEW_MESSAGES && specifications) {
    convertToDateIfNecessary(specifications, 'date')
  }

  return task
}

const STATUS_INTERNAL = 'INTERNAL'

class User {
  constructor(original) {
    Object.keys(original).forEach(property => {
      this[property] = original[property]
    })

    // Pass the household subobject through the same logic as a standard household object.
    if (this.household && typeof this.household === 'object') {
      this.household = household(this.household)
    }

    // Ensure that we always have a roles array at runtime.
    if (!this.roles) {
      this.roles = []
    }

    // Ensure that we always have a preferences object at runtime.
    if (!this.preferences) {
      this.preferences = {}
    }

    // Enforce consistent sorting of child lists.
    if (this.sharedClients) {
      this.sharedClients = clientList(this.sharedClients)
    }

    convertToDateIfNecessary(this, 'createdAt')
  }

  checkRole(acceptableRoles) {
    return this.roles.some(role => acceptableRoles.includes(role))
  }

  isParent() {
    return this.household !== null && this.household !== undefined && this.roles.length === 0
  }

  isCareTeamMember() {
    return this.roles.length === 0 && !this.household && this.sharedClients?.length > 0
  }

  isProvider() {
    return this.affiliation !== null && this.affiliation !== undefined
  }

  role(acceptableRoles) {
    // No acceptable roles indicates that a parent user is expected.
    return (
      this.checkRole(CAN_BE_SUPERUSER) ||
      (acceptableRoles?.length > 0 ? this.checkRole(acceptableRoles) : this.isParent())
    )
  }

  isInternal() {
    return this.status === STATUS_INTERNAL
  }

  isParentOf(clientId) {
    if (!this.household?.clients) {
      return false
    }

    return this.household.clients.find(clientStub => clientStub.id === clientId) !== undefined
  }

  canPlanFor(clientId) {
    return this.isParentOf(clientId) || this.role(CAN_BE_NAVIGATOR)
  }

  goalRedirectLink(householdId, goalId, clientId) {
    // Where we go depends on the current user’s role and a goal ID is provided.
    const pathname = this.isParent()
      ? goalId
        ? `/roadmaps/${clientId}/goals/${goalId}`
        : `/roadmaps/${clientId ?? ''}`
      : householdId
        ? `/client-accounts/${householdId}${clientId ? `/roadmaps/${clientId}${goalId ? `/goals/${goalId}` : ''}` : ''}`
        : '/client-accounts' // Service users need a household ID; if absent, it just goes to the client account list.

    return { pathname }
  }

  // These paths are based on wherever the `iepRoutes` function is called in _navigation/map.js_
  iepRedirectLink(householdId, iepId, clientId) {
    const iepPath = `/ieps/${clientId}/${iepId}/summary`
    const pathname = this.isParent()
      ? iepPath
      : householdId
        ? `/client-accounts/${householdId}${iepPath}`
        : '/client-accounts'

    return { pathname }
  }

  messageLink(messageId, householdId, channelId, clientId) {
    // Where we go depends on the current user’s role and a goal ID is provided.
    const pathname = this.isParent()
      ? this.username === channelId
        ? '/messages/andy'
        : channelId
          ? `/messages/goals/${channelId}`
          : `/messages/navigator`
      : householdId
        ? `/client-accounts/${householdId}${
            channelId && clientId ? `/roadmaps/${clientId}/goals/${channelId}` : '/messages'
          }`
        : '/client-accounts' // Service users need a household ID; if absent, it just goes to the client account list.

    // The state supplies additional information for displaying the message.
    const state =
      this.username === channelId
        ? {
            andyChat: 'open',
            highlightedMessageId: messageId
          }
        : channelId
          ? {
              highlightedGoalId: channelId,
              highlightedCommentId: messageId
            }
          : {
              chat: 'open',
              highlightedMessageId: messageId
            }

    return { pathname, state }
  }

  portalPath(household) {
    // we moved the resourceLink from the localStorage inside the portalPath method
    // as part of [UND-5008] when the user completes the membership purchase
    // then we clear the resourceLink and take the user to /feed page
    const resourceLink = window.localStorage.getItem(RESOURCE_LINK)
    const portalBase = this.isParent()
      ? (resourceLink ?? (household?.clients?.length > 0 ? 'feed' : household ? 'resources' : 'binder'))
      : this.isCareTeamMember()
        ? 'binder'
        : this.isProvider()
          ? (resourceLink ?? 'resources')
          : this.role(CAN_BE_SUPERUSER) // This has to be next because superusers will be true for everything else.
            ? (resourceLink ?? 'client-accounts')
            : this.role(CAN_BE_NAVIGATOR)
              ? (resourceLink ?? 'dashboard')
              : this.role(CAN_BE_CERTIFIED)
                ? 'tasks'
                : this.role(CAN_BE_ADMIN)
                  ? (resourceLink ?? 'tools')
                  : 'not-found' // All other possibilities have been covered, so this indicates a data issue.

    return `/${portalBase}`
  }
}

const user = user => new User(user)

const assetSize = dimension => {
  // Heuristic: we return the size at double the dimension due to HiDPI displays.
  if (dimension <= 100) {
    return ASSET_SIZE_1X
  } else if (dimension <= 200) {
    return ASSET_SIZE_2X
  } else if (dimension <= 300) {
    return ASSET_SIZE_3X
  } else {
    return ASSET_SIZE_ORIGINAL
  }
}

const convertClientAccountDate = source => {
  convertToDateIfNecessary(source, 'kickstartDate')
  convertToDateIfNecessary(source, 'kickstartEndDate')
  convertToDateIfNecessary(source, 'last_boost')
  convertToDateIfNecessary(source, 'next_boost')
  source.child_dob = getBirthdayDate(source.child_dob)
  convertToDateIfNecessary(source, 'last_call')
  convertToDateIfNecessary(source, 'next_call')
  convertToDateIfNecessary(source, 'created_at')
  return source
}

const clientAccount = client => {
  convertClientAccountDate(client)

  return {
    householdId: client.household_id,
    goalId: client.goal_id,
    goalName: client.goal_name,
    goalType: client.goal_type,
    householdName: client.account_name,
    childName: client.child_name || FALLBACK_CHILD_NAME,
    childDOB: client.child_dob,
    roadmapCreated: client.roadmap_created,
    childAge: client.child_age,
    childId: client.child_id,
    level: client.membership_level,
    primaryEmail: client.primary_email,
    trialStart: client.trial_start,
    trialEnd: client.trial_end,
    lastBoost: client.last_boost,
    nextBoost: client.next_boost,
    nextCall: client.next_call,
    lastCall: client.last_call,
    nextIEP: client.next_iep,
    specificTo: client.child_name,
    kickstartDate: client.kickstartDate,
    kickstartEndDate: client.kickstartEndDate,
    harvestLink: client.harvest_link,
    callNotesLink: client.call_notes_link,
    state: client.state,
    createdAt: client.created_at
  }
}

const NUMERIC_KEYS = [
  'page_number',
  'duration_minutes',
  'percent_in_general_ed',
  'percent_outside_general_ed',
  'goal_number'
]

const FRONTEND_KEYS = new Set(['isNew', 'insertAfterId'])

// Keys the IEP extraction backend already sends (and expects back) in camelCase rather than
// snake_case — kept in sync with TECHNICAL_SECTIONS in iep/extraction-editor/constants.js, which
// lists the same keys for a different reason (grouping them in the editor's "Advanced" section).
const CASE_PRESERVED_KEYS = new Set([
  'preVerificationServices',
  'goalToPresentLevelAdjacency',
  'presentLevelToAccommodationModificationAdjacency'
])

// True when `key` must pass through toSnakeCaseKeys/toCamelCaseKeys unchanged rather than being
// case-converted: either it's one of CASE_PRESERVED_KEYS above, or it starts with an underscore
// (e.g. `_sectionClassifications`) — the leading `_` is itself part of the correct key, and
// blindly running it through either regex consumed/reintroduced it incorrectly, silently renaming
// the key on every save (`_sectionClassifications` -> `SectionClassifications` -> back out as
// `_section_classifications`, which the backend doesn't recognize).
const shouldPreserveKeyCase = key => key.startsWith('_') || CASE_PRESERVED_KEYS.has(key)

const toSnakeCaseKeys = obj => {
  if (Array.isArray(obj)) {
    return obj.map(toSnakeCaseKeys)
  }
  if (!isPlainObject(obj)) {
    return obj
  }
  const result = {}
  Object.keys(obj).forEach(key => {
    if (FRONTEND_KEYS.has(key)) {
      return
    }
    if (key === 'id' && obj[key] === null) {
      return
    }
    if (shouldPreserveKeyCase(key)) {
      result[key] = toSnakeCaseKeys(obj[key])
      return
    }
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
    let val = toSnakeCaseKeys(obj[key])
    if (NUMERIC_KEYS.includes(snakeKey)) {
      val = val === '' || val == null ? null : parseInt(val, 10)
    }
    result[snakeKey] = val
  })
  return result
}

const toCamelCaseKeys = obj => {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCaseKeys)
  }
  if (!isPlainObject(obj)) {
    return obj
  }
  const result = {}
  Object.keys(obj).forEach(key => {
    if (shouldPreserveKeyCase(key)) {
      result[key] = toCamelCaseKeys(obj[key])
      return
    }
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    result[camelKey] = toCamelCaseKeys(obj[key])
  })
  return result
}

const serializeExtractionItem = item => toSnakeCaseKeys(item)

const model = {
  assetSize,
  contact,
  post,
  event,
  goal,
  guide,
  household,
  iep,
  ipp,
  step,
  task,
  user,
  clientAccount,
  serializeExtractionItem,
  toCamelCaseKeys,
  toSnakeCaseKeys
}

export default model
